import * as React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import * as mixins from '@helpcenter/styles/src/mixins';
import type { MessageDescriptor, WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { EmptyStar as EStar, FullStar as FStar } from '../star';
import messages from './messages';

export interface StarRatingProps {
    value: number;
    onChange: (rating: number) => void;
    commentSubmitting: boolean;
}

export class StarRating extends React.Component<StarRatingProps & WrappedComponentProps> {
    static readonly maxStars: number = 5;

    labels: MessageDescriptor[] = [
        messages.zeroStarRating,
        messages.oneStarRating,
        messages.twoStarRating,
        messages.threeStarRating,
        messages.fourStarRating,
        messages.fiveStarRating,
    ];

    handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
    }

    createStar = (rating: number, index: number) => {
        const { intl } = this.props;
        const label = intl.formatMessage(this.labels[index]);
        const id = `${index}-star`;
        const checked = rating === index;
        const changeHandler = () => {
            const { onChange, commentSubmitting } = this.props;
            if (!commentSubmitting) {
                onChange(index);
            }
        };
        if (index === 0) {
            if (rating === 0) {
                return (
                    <React.Fragment>
                        {/* eslint-disable-next-line styled-components-a11y/label-has-associated-control, @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve */}
                        <StarLabel key={`${index}-label`} className="star" htmlFor={id} />
                        <HiddenInput
                            key={`${index}-input`}
                            type="radio"
                            id={id}
                            name="star-rating"
                            checked={checked}
                            onChange={changeHandler}
                        />
                    </React.Fragment>
                );
            }
            return undefined;
        }
        return (
            <React.Fragment>
                <StarLabel
                    key={`${index}-label`}
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                    className="star"
                    htmlFor={id}
                    data-test-id={`${id}-rating`}
                    data-test-checked={checked ? `${id}-rating-checked` : undefined}
                >
                    <EmptyStar label={label} />
                    <FullStar label={label} />
                </StarLabel>
                <HiddenInput
                    key={`${index}-input`}
                    type="radio"
                    id={id}
                    name="star-rating"
                    checked={checked}
                    onChange={changeHandler}
                />
            </React.Fragment>
        );
    };

    getRating = () => {
        let rating: number;
        const propRating = this.props.value;
        if (propRating < 0) {
            rating = 0;
        } else if (propRating > 5) {
            rating = 5;
        } else {
            rating = propRating;
        }
        return rating;
    };

    render() {
        const rating = this.getRating();

        return (
            <StarRatingContainer>
                {this.createStar(rating, 0)}
                {this.createStar(rating, 1)}
                {this.createStar(rating, 2)}
                {this.createStar(rating, 3)}
                {this.createStar(rating, 4)}
                {this.createStar(rating, 5)}
            </StarRatingContainer>
        );
    }
}

export default injectIntl(StarRating);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const EmptyStar = styled(EStar)({});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FullStar = styled(FStar)({});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const StarLabel = styled.label`
    cursor: pointer;

    ${FullStar} {
        display: inline-block;
    }

    ${EmptyStar} {
        display: none;
    }
`;
// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const HiddenInput = styled.input`
    ${mixins.hideVisually};
    display: inline-block;

    &:checked ~ ${StarLabel} ${EmptyStar} {
        display: inline-block;
    }

    &:checked ~ ${StarLabel} ${FullStar} {
        display: none;
    }

    &:focus ~ ${StarLabel} ${EmptyStar} {
        display: inline-block;
    }

    &:focus ~ ${StarLabel} ${FullStar} {
        display: none;
    }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const StarRatingContainer = styled.div`
    display: inline-block;

    /* stylelint-disable-next-line selector-type-no-unknown */
    ${StarLabel}:hover ~ ${StarLabel} ${EmptyStar}, ${HiddenInput}:focus ~ ${StarLabel} ${EmptyStar} {
        display: inline-block;
    }

    /* stylelint-disable-next-line selector-type-no-unknown */
    ${StarLabel}:hover ~ ${StarLabel} ${FullStar}, ${HiddenInput}:focus ~ ${StarLabel} ${FullStar} {
        display: none;
    }

    /* stylelint-disable-next-line selector-type-no-unknown */
    :hover ${HiddenInput}:checked ~ ${StarLabel} ${EmptyStar} {
        display: none;
    }

    /* stylelint-disable-next-line selector-type-no-unknown */
    :hover ${HiddenInput}:checked ~ ${StarLabel} ${FullStar} {
        display: inline-block;
    }

    /* stylelint-disable-next-line selector-type-no-unknown */
    :hover ${StarLabel}:hover ~ ${StarLabel} ${EmptyStar} {
        display: inline-block;
    }

    /* stylelint-disable-next-line selector-type-no-unknown */
    :hover ${StarLabel}:hover ~ ${StarLabel} ${FullStar} {
        display: none;
    }
`;
